import { useEffect, useState } from 'react'
import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { apiConstants, getGeneric } from '../../common/constants'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import Loader from '../../common/Loader'
import { formatDate } from '../../common/util'
import OrderRowActions from '../../common/OrderRowActions'

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_COLOURS = {
  IN_PROGRESS:    { color: '#a16207', bg: '#fef3c7', label: 'Processing' },
  COMPLETED:      { color: '#15803d', bg: '#dcfce7', label: 'Completed' },
  PARTIAL_SUCCESS:{ color: '#c2410c', bg: '#ffedd5', label: 'Partial Success' },
  FAILED:         { color: '#b91c1c', bg: '#fee2e2', label: 'Failed' },
  PENDING:        { color: '#1e40af', bg: '#dbeafe', label: 'Pending' },
}

function deriveStatus(row) {
  const progress   = row?.fileProgress
  const execFailed = row?.executionStatus?.failed || 0
  const errorCount = row?.metaDataObject?.errorCount || 0
  if (progress === 'COMPLETED' && (execFailed > 0 || errorCount > 0)) return 'PARTIAL_SUCCESS'
  if (progress) return progress
  return 'PENDING'
}

function StatusBadge({ row }) {
  const key = deriveStatus(row)
  const cfg = STATUS_COLOURS[key] || { color: '#6b7280', bg: '#f3f4f6', label: key }
  return (
    <Box component='span' sx={{
      display: 'inline-block', px: '7px', py: '2px', borderRadius: '4px',
      fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
      bgcolor: cfg.bg, color: cfg.color,
    }}>
      {cfg.label}
    </Box>
  )
}

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold, color }) => (
  <Box sx={{
    fontSize: 12.5, lineHeight: 1.4,
    color: color || (muted ? 'text.secondary' : 'text.primary'),
    fontWeight: bold ? 700 : 400,
  }}>
    {children}
  </Box>
)

const DataLabel = ({ children }) => (
  <Box component='span' sx={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', mr: '4px' }}>
    {children}
  </Box>
)

// ─── TriggerButton styled inline (same look as OrderRowActions single-action) ─

function FileButton({ icon, label, onClick }) {
  return (
    <Box component='button' onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        px: '8px', py: '3px', borderRadius: '4px',
        fontSize: '11px', fontWeight: 600, cursor: 'pointer',
        bgcolor: '#e3f2fd', color: '#1565c0', border: '1px solid #bfdbfe',
        transition: 'all 0.15s',
        '&:hover': { bgcolor: '#1565c0', color: '#fff', borderColor: '#1565c0' },
      }}>
      {icon}
      {label}
    </Box>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MisUploadHistory() {
  const [rows, setRows]         = useState([])
  const [count, setCount]       = useState(0)
  const [page, setPage]         = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [loading, setLoading]   = useState(false)
  const [reload, setReload]     = useState(0)

  useEffect(() => {
    setLoading(true)
    getGeneric(apiConstants.WEIGHT_LEDGER_MIS_HISTORY, { limit: sizePerPage, skip: page * sizePerPage })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data || res?.data?.responseObj || []
        setRows(list)
        setCount(res?.data?.count ?? list.length)
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))
  }, [page, sizePerPage, reload])

  const columns = [
    {
      field: 'uuid',
      headerName: 'Upload ID', headerAlign: 'left', align: 'left',
      hideSortIcons: true, minWidth: 130, flex: 0.9,
      renderCell: ({ row }) => {
        const id = row.uuid || row.id
        return (
          <Tooltip title={id || ''}>
            <Box sx={{ fontSize: 12, fontWeight: 600, color: '#3b82f6', cursor: 'default' }}>
              {id ? `${id.slice(0, 10)}…` : '—'}
            </Box>
          </Tooltip>
        )
      },
    },
    {
      field: 'remark',
      headerName: 'Remark / Tag', headerAlign: 'left', align: 'left',
      hideSortIcons: true, minWidth: 150, flex: 1,
      renderCell: ({ row }) => <CellLine muted>{row.remark || row.misFileTag || '—'}</CellLine>,
    },
    {
      field: 'createdBy',
      headerName: 'Uploaded By / At', headerAlign: 'left', align: 'left',
      hideSortIcons: true, minWidth: 160, flex: 1,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, py: 0.5 }}>
          <CellLine bold>{row.createdBy || '—'}</CellLine>
          <CellLine muted>{row.createdAt ? formatDate(row.createdAt, { format: 'dd MMM yyyy HH:mm' }) : '—'}</CellLine>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status', headerAlign: 'left', align: 'left',
      hideSortIcons: true, width: 140,
      renderCell: ({ row }) => <StatusBadge row={row} />,
    },
    {
      field: 'stats',
      headerName: 'Stats', headerAlign: 'left', align: 'left',
      hideSortIcons: true, minWidth: 160, flex: 1.1,
      renderCell: ({ row }) => {
        const m = row.metaDataObject || {}
        const errors = m.errorCount ?? 0
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, py: 0.5 }}>
            <Box sx={{ fontSize: '12px', color: 'text.primary' }}><DataLabel>Total</DataLabel>{m.totalValidRows ?? '—'}</Box>
            <Box sx={{ fontSize: '12px', color: '#15803d' }}><DataLabel>Updated</DataLabel>{m.updatedCount ?? '—'}</Box>
            <Box sx={{ fontSize: '12px', color: '#a16207' }}><DataLabel>Skipped</DataLabel>{m.skippedCount ?? '—'}</Box>
            <Box sx={{ fontSize: '12px', color: errors > 0 ? '#b91c1c' : '#6b7280' }}><DataLabel>Errors</DataLabel>{errors}</Box>
          </Box>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Files', headerAlign: 'center', align: 'center',
      hideSortIcons: true, width: 60,
      sortable: false,
      renderCell: ({ row }) => {
        const original    = row.s3RefToFile?.signedLink
        const errorReport = row.s3LinkExcelResponse?.signedLink
        const errorCount  = row.metaDataObject?.errorCount ?? 0
        const actions = [
          ...(original ? [{
            key: 'original',
            label: 'Download original file',
            icon: <DescriptionOutlinedIcon fontSize='small' />,
            onClick: () => window.open(original, '_blank'),
          }] : []),
          ...(errorCount > 0 && errorReport ? [{
            key: 'errors',
            label: 'Download error report',
            icon: <DownloadIcon fontSize='small' />,
            onClick: () => window.open(errorReport, '_blank'),
            color: 'error',
            dividerBefore: original ? true : false,
          }] : []),
        ]
        if (!actions.length) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>
        return <OrderRowActions actions={actions} />
      },
    },
  ]

  return (
    <Box>
      {loading && <Loader />}
      <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>Upload History</Typography>
            <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>Past MIS uploads with their processing outcomes</Typography>
          </Box>
          <Box
            component='button'
            onClick={() => setReload((n) => n + 1)}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', px: '10px', py: '6px', borderRadius: '6px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc', color: '#64748b', cursor: 'pointer', fontSize: '12px', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9', color: '#1e293b' } }}
          >
            <RefreshIcon sx={{ fontSize: 15 }} /> Refresh
          </Box>
        </Box>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <ProzoDataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.uuid || r.id}
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            rowCount={count}
            paginationMode='server'
            getRowHeight={() => 'auto'}
          />
        </CardContent>
      </Card>
    </Box>
  )
}
